'use client';

import { useState, useEffect, useId, useRef } from 'react';
import { TextInput, Paper, Box, Group, Text, Image } from '@mantine/core';
import { IconSearch, IconArrowRight } from '@tabler/icons-react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import api from '@/lib/api';
import { notifyError } from '@/lib/notify';
import { getThumbUrl } from '@/lib/imageUrl';
import { useHomeSearch } from '@/context/HomeSearchContext';

// Compact set search shown in the header.
// - On other pages: a normal typeahead that jumps to a set / to the homepage grid.
// - On the homepage: mirrors the big search bar's value and only appears once
//   that bar has scrolled out of view (they share the same query).
export function HeaderSearch({ mobile = false, onNavigate }) {
  const pathname = usePathname();
  const router = useRouter();
  const isHome = pathname === '/';
  const home = useHomeSearch();
  const instanceId = useId().replaceAll(':', '');
  const inputId = `header-search-${instanceId}`;
  const listboxId = `${inputId}-suggestions`;
  const statusId = `${inputId}-status`;

  // Local query for non-home pages. On home we read/write the shared value.
  const [localQuery, setLocalQuery] = useState('');
  const query = isHome ? (home?.value ?? '') : localQuery;

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapperRef = useRef(null);

  const closeSuggestions = () => {
    setOpen(false);
    setActive(-1);
  };

  const finishNavigation = () => {
    closeSuggestions();
    onNavigate?.();
  };

  const navigateTo = (path) => {
    finishNavigation();
    router.push(path);
  };

  const setQuery = (value) => {
    if (isHome) {
      home?.setQuery(value);
    } else {
      setLocalQuery(value);
    }
  };

  useEffect(() => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setOpen(false);
      setActive(-1);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const data = await api.fetchSets(null, query, 6, 0, 'popular', controller.signal);
        if (controller.signal.aborted) return;
        const sets = data.sets || [];
        setSuggestions(sets);
        setOpen(sets.length > 0);
        setActive(-1);
      } catch (err) {
        if (err.name === 'AbortError' || controller.signal.aborted) return;
        console.error('Header search failed:', err);
        notifyError(err, { title: 'Search failed', id: 'header-search' });
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 200);
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [query]);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) closeSuggestions();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Reset the local query when navigating between non-home pages.
  useEffect(() => {
    if (!isHome) setLocalQuery('');
    setOpen(false);
  }, [pathname, isHome]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (open && active >= 0 && suggestions[active]) {
        e.preventDefault();
        navigateTo(`/set/${suggestions[active].id}`);
      } else if (query.trim() && !isHome) {
        e.preventDefault();
        navigateTo(`/?search=${encodeURIComponent(query.trim())}`);
      } else {
        closeSuggestions();
        if (mobile && query.trim()) onNavigate?.();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      if (suggestions.length === 0) return;
      e.preventDefault();
      setOpen(true);
      setActive(i => (!open || i < 0 ? 0 : (i + 1) % suggestions.length));
    } else if (e.key === 'ArrowUp') {
      if (suggestions.length === 0) return;
      e.preventDefault();
      setOpen(true);
      setActive(i => (!open || i < 0 ? suggestions.length - 1 : (i - 1 + suggestions.length) % suggestions.length));
    } else if (e.key === 'Escape') {
      if (open) e.preventDefault();
      closeSuggestions();
    } else if (e.key === 'Tab') {
      closeSuggestions();
    }
  };

  // On the homepage the header search only shows once the big search bar is
  // out of view. On other pages it always shows (until the responsive breakpoint).
  const shouldRender = mobile || !isHome || (home && !home.mainSearchVisible);
  if (!shouldRender) return null;

  const listboxOpen = open && suggestions.length > 0;
  const activeOptionId = listboxOpen && active >= 0
    ? `${listboxId}-option-${active}`
    : undefined;

  return (
    <div
      ref={wrapperRef}
      className={`header-search${mobile ? ' mobile-header-search' : ''}`}
      style={{ position: 'relative' }}
    >
      <TextInput
        id={inputId}
        placeholder="Search sets..."
        size="sm"
        radius="xl"
        value={query}
        onChange={(e) => {
          setQuery(e.currentTarget.value);
          setActive(-1);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0 && query.length >= 2) setOpen(true); }}
        role="combobox"
        aria-label="Search LEGO sets"
        aria-autocomplete="list"
        aria-expanded={listboxOpen}
        aria-controls={listboxOpen ? listboxId : undefined}
        aria-activedescendant={activeOptionId}
        aria-describedby={statusId}
        aria-busy={loading}
        leftSection={<IconSearch size={16} aria-hidden="true" style={{ color: '#66707a' }} />}
        classNames={{ input: 'header-search-input' }}
      />
      <span id={statusId} className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {loading
          ? 'Searching LEGO sets.'
          : query.length >= 2
            ? suggestions.length === 0
              ? 'No matching LEGO sets found.'
              : `${suggestions.length} matching ${suggestions.length === 1 ? 'set' : 'sets'} available.`
            : 'Enter at least two characters to search.'}
      </span>
      {listboxOpen && (
        <Paper
          id={listboxId}
          role="listbox"
          aria-label="Matching LEGO sets"
          shadow="lg"
          radius="lg"
          style={{
            position: 'absolute', top: '100%', right: 0, left: mobile ? 0 : 'auto', zIndex: 400,
            marginTop: 8, width: mobile ? '100%' : 360, maxWidth: mobile ? '100%' : '80vw',
            border: '1px solid #e9ecef', overflow: 'hidden',
          }}
        >
          {suggestions.map((sug, i) => (
            <Link
              key={sug.id}
              id={`${listboxId}-option-${i}`}
              href={`/set/${sug.id}`}
              role="option"
              aria-selected={i === active}
              tabIndex={-1}
              className="search-suggestion"
              data-active={i === active}
              onClick={finishNavigation}
              onMouseEnter={() => setActive(i)}
            >
              <Box style={{
                width: 44, height: 44, borderRadius: 8, backgroundColor: '#f8f9fa',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden',
              }}>
                <Image
                  src={sug.image_url ? getThumbUrl(sug.image_url, 96) : 'https://placehold.co/36x36?text=?'}
                  alt=""
                  fit="contain" w={36} h={36}
                  style={{ mixBlendMode: 'multiply' }}
                />
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={600} lineClamp={1} style={{ color: '#212529' }}>{sug.name}</Text>
                <Group gap={6}>
                  <Text size="xs" c="dimmed" fw={600}>#{sug.id}</Text>
                  {sug.theme && <Text size="xs" c="dimmed">· {sug.theme}</Text>}
                </Group>
              </Box>
              <IconArrowRight size={14} color="#adb5bd" style={{ flexShrink: 0 }} />
            </Link>
          ))}
        </Paper>
      )}
    </div>
  );
}
